/**
 * The body's facts (D234, actors round D) — what is left of the player and
 * of every foe, whether a foe is hostile, and what is in the player's hand
 * (`docs/design/actors.md` §6; Mike's rulings 2–4 of 2026-10-04).
 *
 * @remarks
 * One module because every reader of a fight — the strike handler, the
 * tick's agent step, the two conditions, the describer that reads a fallen
 * body — must agree about three things, and each is one rule read from
 * several ends:
 *
 * - **What is left.** `world.actorHealth` and `world.playerHealth` are
 *   DELTAS from the declarations (the "no derived data" rule, as
 *   `actorLocations` is for where a body stands): a body absent from the
 *   record is whole, a player with no entry has the manifest's health. A
 *   foe is DEFEATED when what is left is nothing; it stays where it fell,
 *   referable, and never strikes or blocks again. Both are the world's for
 *   the checkpoint (ruling 4): a photograph holds exactly what stood, and a
 *   restore puts back exactly that — never full.
 * - **Hostility** is a foe's `hostileWhen` read now, while it stands — a
 *   fallen body is hostile to nobody. "Attackable" is a different and
 *   wider question (ruling 3), answered by the `combat` block's presence
 *   alone: the loader has proved the condition can come true.
 * - **The hand.** With no `with`, the player strikes with the best thing in
 *   hand — WIELDED (an `equippable` weapon that is equipped) or carried and
 *   needing no wielding (a weapon that declares no `equippable`: the pry
 *   bar is in the hand the moment it is picked up) — else bare hands
 *   (`player.unarmedDamage`). An equippable weapon merely carried is not in
 *   hand: D143's rule, stated for the mask, that a wieldable thing must be
 *   wielded. Armor is simpler — it counts only while worn, by construction
 *   (the schema requires `equippable`), and it sums.
 *
 * The arithmetic is here too, so it is written once: fixed, floored at zero,
 * no roll (ruling 2).
 *
 * @packageDocumentation
 */

import type { ActorCombat, ActorDefinition, ActorId, ItemDefinition, ItemId, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor, requireItem } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { isActorPresent } from './actor-presence.js';
import { withWorld } from './item-state.js';

/** What is in the player's hand for a strike: a weapon by id, or bare hands. */
export interface StrikingHand {
	/** What the strike costs the foe before its `defense`. */
	readonly damage: number;
	/** The weapon, or `undefined` for bare hands. */
	readonly weaponItemId?: ItemId;
}

/**
 * The damage one strike does, fixed and floored (ruling 2): what is swung,
 * less what is turned, never below nothing.
 *
 * @param damage - The striker's damage
 * @param defense - The struck's defense
 * @returns What the strike costs
 */
export const strikeDamageOf = (damage: number, defense: number): number => Math.max(0, damage - defense);

/**
 * The foe's combat block, or nothing — the one question "is this a foe" is
 * answered by.
 *
 * @param actor - The actor
 * @returns Its combat block, when it has one
 */
export const combatOf = (actor: ActorDefinition): ActorCombat | undefined => actor.combat;

/**
 * What is left of an actor's health — its declared health less what it has
 * taken; `undefined` for a body with no `combat` block, which has no health
 * to lose.
 *
 * @param bundle - The loaded bundle (the declarations)
 * @param state - The live runtime state (the deltas)
 * @param actorId - The actor
 * @returns What is left, or `undefined` for a body that is not a foe
 */
export const healthOfActor = (bundle: EngineGameBundle, state: RuntimeState, actorId: ActorId): number | undefined =>
	state.world.actorHealth[actorId] ?? combatOf(requireActor(bundle, actorId))?.health;

/**
 * Is the actor DEFEATED — a foe with nothing left? A body that was never a
 * foe never falls.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param actorId - The actor
 * @returns Whether the body has fallen
 */
export const isActorDefeated = (bundle: EngineGameBundle, state: RuntimeState, actorId: ActorId): boolean => {
	const remaining = healthOfActor(bundle, state, actorId);

	return remaining !== undefined && remaining <= 0;
};

/**
 * Has the player ever STRUCK the actor, in this world (D234's ledger, the
 * read `actor_attacked` makes)?
 *
 * @param state - The live runtime state
 * @param actorId - The actor
 * @returns Whether the body is in the struck ledger
 */
export const wasActorAttacked = (state: RuntimeState, actorId: ActorId): boolean => state.world.attackedActorIds.includes(actorId);

/**
 * Is the actor HOSTILE right now — a standing foe whose `hostileWhen` holds?
 * Presence is the tick's second question, asked separately, because a
 * hostile body in another room is a fact a pack may read (it is coming)
 * even though it cannot strike from there.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param actorId - The actor
 * @returns Whether the foe would strike if it could reach
 */
export const isActorHostile = (bundle: EngineGameBundle, state: RuntimeState, actorId: ActorId): boolean => {
	const combat = combatOf(requireActor(bundle, actorId));

	return combat !== undefined && !isActorDefeated(bundle, state, actorId) && evaluateCondition(combat.hostileWhen, bundle, state);
};

/** A foe: a body and the combat block that makes it one, paired so no reader has to ask twice. */
export interface Foe {
	readonly actor: ActorDefinition;
	readonly combat: ActorCombat;
}

/**
 * The foes that strike this turn: every hostile body within reach, in the
 * pack's declared order — the tick's agent step reads this.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The hostile, present foes in declared order
 */
export const hostileFoesPresent = (bundle: EngineGameBundle, state: RuntimeState): readonly Foe[] =>
	bundle.contentPack.actors.flatMap((actor) =>
		actor.combat !== undefined && isActorHostile(bundle, state, actor.actorId) && isActorPresent(bundle, state, actor.actorId)
			? [{ actor, combat: actor.combat }]
			: [],
	);

/**
 * The state with the actor's remaining health written — as a delta: a body
 * made whole again leaves the record.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before
 * @param actorId - The actor
 * @param remaining - What is left, floored at zero by the caller
 * @returns The state after
 */
export const withActorHealth = (bundle: EngineGameBundle, state: RuntimeState, actorId: ActorId, remaining: number): RuntimeState => {
	const { [actorId]: _previous, ...others } = state.world.actorHealth;
	const declared = combatOf(requireActor(bundle, actorId))?.health;

	return withWorld(state, { actorHealth: remaining === declared ? others : { ...others, [actorId]: remaining }});
};

/**
 * The state with the actor in the struck ledger — once; a second strike
 * writes nothing new.
 *
 * @param state - The state before
 * @param actorId - The actor
 * @returns The state after
 */
export const withActorAttacked = (state: RuntimeState, actorId: ActorId): RuntimeState =>
	wasActorAttacked(state, actorId) ? state : withWorld(state, { attackedActorIds: [...state.world.attackedActorIds, actorId]});

/**
 * What is left of the player's health — the manifest's less what has been
 * taken; `undefined` in a pack that declares no body, where no foe can
 * strike.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns What is left, or `undefined` where the pack has no combat
 */
export const playerHealthOf = (bundle: EngineGameBundle, state: RuntimeState): number | undefined =>
	state.world.playerHealth ?? bundle.contentPack.manifest.player?.health;

/**
 * The state with the player's remaining health written — as a delta: a
 * player made whole leaves the record.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before
 * @param remaining - What is left, floored at zero by the caller
 * @returns The state after
 */
export const withPlayerHealth = (bundle: EngineGameBundle, state: RuntimeState, remaining: number): RuntimeState => {
	const { playerHealth: _previous, ...world } = state.world;

	return { ...state, world: remaining === bundle.contentPack.manifest.player?.health ? world : { ...world, playerHealth: remaining }};
};

/** The items the player wears, in slot order. */
const wornItemsOf = (bundle: EngineGameBundle, state: RuntimeState): readonly ItemDefinition[] =>
	Object.values(state.world.equippedItemIds).map((itemId) => requireItem(bundle, itemId));

/**
 * What the player's worn armor turns of a strike — every equipped `armor`,
 * summed. Nothing worn, nothing turned.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The player's defense
 */
export const wornDefenseOf = (bundle: EngineGameBundle, state: RuntimeState): number =>
	wornItemsOf(bundle, state).reduce((defense, item) => defense + (item.armor?.defense ?? 0), 0);

/**
 * Is a carried weapon IN HAND — wielded if it is wieldable, in the hand by
 * being carried if it is not?
 *
 * @param state - The live runtime state
 * @param item - A carried item declaring `weapon`
 * @returns Whether a strike may use it without saying `with`
 */
const isWeaponInHand = (state: RuntimeState, item: ItemDefinition): boolean =>
	item.equippable === undefined || Object.values(state.world.equippedItemIds).includes(item.itemId);

/**
 * What the player strikes with when they name nothing: the best weapon in
 * hand, else bare hands — see the module remarks.
 *
 * @remarks
 * "Best" is the greatest `damage`; a tie keeps the earlier — bare hands,
 * then the carry order — so the answer is a function of the state and never
 * of iteration luck.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @returns The striking hand
 */
export const strikingHandOf = (bundle: EngineGameBundle, state: RuntimeState): StrikingHand => {
	const bareHands: StrikingHand = { damage: bundle.contentPack.manifest.player?.unarmedDamage ?? 0 };

	return state.world.inventoryItemIds
		.map((itemId) => requireItem(bundle, itemId))
		.flatMap((item): readonly StrikingHand[] =>
			item.weapon !== undefined && isWeaponInHand(state, item) ? [{ damage: item.weapon.damage, weaponItemId: item.itemId }] : [],
		)
		.reduce<StrikingHand>((best, inHand) => (inHand.damage > best.damage ? inHand : best), bareHands);
};
